import { BACKEND_ERROR_CODE, createFlatRequest, createRequest } from '@sa/axios';
import { useAuthStore } from '@/store/modules/auth';
import { localStg } from '@/utils/storage';
import { getServiceBaseURL } from '@/utils/service';
import { $t } from '@/locales';
import { getCookie } from '@/utils/common';
import { useMessage } from 'naive-ui';

const isHttpProxy = import.meta.env.DEV && import.meta.env.VITE_HTTP_PROXY === 'Y';
const { baseURL, otherBaseURL } = getServiceBaseURL(import.meta.env, isHttpProxy);

interface InstanceState {
    /** whether the request is refreshing token */
    isRefreshingToken: boolean;
}

const csrfToken = getCookie('csrftoken');

export const request = createFlatRequest<App.Service.Response, InstanceState>(
    {
        baseURL,
        headers: {
            apifoxToken: 'XL299LiMEDZ0H5h3A29PxwQXdMJqWyY2',
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken
        },
        timeout: 100000, // 100 second timeout
        // Disable axios-retry to prevent timeout delays on errors
        'axios-retry': {
            retries: 0
        }
    },
    {
        async onRequest(config) {
            const { headers } = config;

            // set token
            // const token = localStg.get('token');
            // const Authorization = token ? `Bearer ${token}` : null;
            Object.assign(headers);

            return config;
        },

        isBackendSuccess(response) {
            // when the backend response status is 2xx, it means the request is success
            // 200 = OK, 201 = Created, 204 = No Content, etc.
            return response.status >= 200 && response.status < 300;
        },

        async onBackendFail(response, instance) {
            const authStore = useAuthStore();

            function handleLogout() {
                authStore.resetStore();
            }

            function logoutAndCleanup() {
                handleLogout();
                window.removeEventListener('beforeunload', handleLogout);
            }

            // when the backend response code is in `logoutCodes`, it means the user will be logged out and redirected to login page
            const logoutCodes = import.meta.env.VITE_SERVICE_LOGOUT_CODES?.split(',') || [];
            if (logoutCodes.includes(response.data.code)) {
                handleLogout();
                return null;
            }

            // when the backend response code is in `modalLogoutCodes`, it means the user will be logged out by displaying a modal
            const modalLogoutCodes = import.meta.env.VITE_SERVICE_MODAL_LOGOUT_CODES?.split(',') || [];
            if (modalLogoutCodes.includes(response.data.code)) {
                // prevent the user from refreshing the page
                window.addEventListener('beforeunload', handleLogout, { passive: true });

                window.$dialog?.error({
                    title: 'Error',
                    content: response.data.msg,
                    positiveText: $t('common.confirm'),
                    maskClosable: false,
                    onPositiveClick() {
                        logoutAndCleanup();
                    },
                    onClose() {
                        logoutAndCleanup();
                    }
                });

                return null;
            }


            return null;
        },
        transformBackendResponse(response) {
            return response.data;
        },
        onError(error) {
            // Handle errors immediately without delay
            // dont use this error.message; heer
            let message = error.message;
            let backendErrorCode = '404';

            // get backend error message and code
            if (error.code === BACKEND_ERROR_CODE) {
                // message = error.response?.data?.msg || message;
                backendErrorCode = error.response?.data?.code || '';
            }

            // Enhanced 429 rate limiting error handling
            if (error.response?.status === 429) {
                const config = error.config;
                const metadata = (config as any)?.metadata as App.Request.RequestMetadata | undefined;
                const operationType = metadata?.operationType || 'read';
                const isUserInitiated = metadata?.isUserInitiated ?? true;
                const operationName = metadata?.operationName || 'Unknown operation';

                console.warn(`Rate limit exceeded (429) for ${operationType} operation: ${operationName}`);

                // Handle based on operation type
                if (operationType === 'write' && isUserInitiated) {
                    // Show user-friendly message for write operations
                    try {
                        const message = useMessage();
                        message.warning($t('billing.tooManyRequests'));
                        console.log(`[429 Handler] Showing user message for write operation: ${operationName}`);
                    } catch (msgError) {
                        console.warn('[429 Handler] Could not show user message:', msgError);
                        // Fallback: log the user-friendly message
                        console.warn('Rate limit exceeded: Too many requests, please try again later');
                    }
                } else {
                    // Silent handling for read operations (polling)
                    console.log(`[429 Handler] Silent handling for read operation: ${operationName}`);
                }

                // Create enhanced error object with metadata
                const rateLimitError: App.Request.RateLimitError = {
                    status: 429,
                    message: 'Rate limit exceeded',
                    operationType,
                    isUserInitiated
                };

                // Attach enhanced error info to the original error
                (error as any).rateLimitInfo = rateLimitError;
                return;
            }

            // the error message is displayed in the modal
            const modalLogoutCodes = import.meta.env.VITE_SERVICE_MODAL_LOGOUT_CODES?.split(',') || [];
            if (modalLogoutCodes.includes(backendErrorCode)) {
                return;
            }

            // For client errors (4xx), check if this is a user-initiated operation that should show errors
            if (error.response?.status && error.response.status >= 400 && error.response.status < 500) {
                const config = error.config;
                const metadata = (config as any)?.metadata as App.Request.RequestMetadata | undefined;
                const showUserErrors = metadata?.showUserErrors ?? false;
                
                console.warn(`Client error ${error.response.status}: ${message}`);
                
                // For user-initiated operations that should show errors, re-throw to let caller handle
                if (showUserErrors) {
                    throw error;
                }
                
                // For silent operations, just return
                return;
            }

            // // Only show error messages for server errors or network issues
            // if (!error.response || error.response.status >= 500) {
            //   window.$message?.error?.(message);
            // }
        }
    }
);

export const demoRequest = createRequest<App.Service.DemoResponse>(
    {
        baseURL: otherBaseURL.demo
    },
    {
        async onRequest(config) {
            const { headers } = config;

            // set token
            const token = localStg.get('token');
            const Authorization = token ? `Bearer ${token}` : null;
            Object.assign(headers, { Authorization });

            return config;
        },
        isBackendSuccess(response) {
            // when the backend response code is "200", it means the request is success
            // you can change this logic by yourself
            return response.data.status === '200';
        },
        async onBackendFail(_response) {
            // when the backend response code is not "200", it means the request is fail
            // for example: the token is expired, refresh token and retry request
        },
        transformBackendResponse(response) {
            return response.data.result;
        },
        onError(error) {
            // when the request is fail, you can show error message

            let message = error.message;

            // show backend error message
            if (error.code === BACKEND_ERROR_CODE) {
                message = error.response?.data?.message || message;
            }

            window.$message?.error(message);
        }
    }
);
